import { CheckCircle2, CircleDot, TriangleAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatCredentialTimestamp } from "../../lib/format-date";
import { VerificationSourceLink } from "../verification-source-link";
import { ScreeningDetails } from "./screening-details";
import { ScreeningEvidence } from "./screening-evidence";
import {
  type ProviderScreening,
  type ReportIssue,
  type ScreeningScope,
} from "./types";

const IMPORTED_RESULT_LABELS = {
  clear: "Reported no exclusions",
  concern: "Reported match — review needed",
  "missing-info": "Reported incomplete result",
  error: "Reported check failure",
} as const;

const STATUS_STYLES = {
  clear: "bg-green-50 text-green-800 dark:bg-green-950/30 dark:text-green-200",
  "attention-needed":
    "bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200",
  limited:
    "bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200",
  incomplete:
    "bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200",
  "in-progress":
    "bg-blue-50 text-blue-800 dark:bg-blue-950/30 dark:text-blue-200",
} as const;
const STATUS_ICONS = {
  clear: CheckCircle2,
  "attention-needed": TriangleAlert,
  limited: TriangleAlert,
  incomplete: TriangleAlert,
  "in-progress": CircleDot,
} as const;

export function ScreeningCard({
  screening,
  scope,
  embedded = false,
  issues,
}: {
  screening: ProviderScreening;
  scope: ScreeningScope;
  embedded?: boolean;
  issues?: ReportIssue[];
}) {
  const latest = screening.latest;
  const assessment = latest?.assessment;
  const StatusIcon = assessment ? STATUS_ICONS[assessment.status] : CircleDot;
  const imported = screening.imported;
  const program =
    "program" in screening && typeof screening.program === "string"
      ? screening.program
      : undefined;
  // Without a result the link falls back to the source's homepage. In a
  // report that check's manual-lookup steps already link the same site.
  let sourceUrl = latest?.result?.facts.sourceUrl;
  if (!sourceUrl && !embedded) {
    sourceUrl = screening.sourceUrl;
  }
  return (
    <section
      aria-label={screening.label}
      className={cn("space-y-3", !embedded && "rounded-md border p-3")}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="space-y-1">
          <h4 className="text-sm font-medium">{screening.label}</h4>
          {latest?.collectedAt ? (
            <p className="text-xs text-muted-foreground">
              Checked {formatCredentialTimestamp(latest.collectedAt)}
            </p>
          ) : (
            latest && (
              <p className="text-xs text-muted-foreground">
                Requested {formatCredentialTimestamp(latest.requestedAt)}
              </p>
            )
          )}
        </div>
        <VerificationSourceLink sourceUrl={sourceUrl} />
      </div>
      {!embedded && assessment && (
        <div
          role="status"
          className={cn(
            "flex items-start gap-2 rounded-md p-3",
            STATUS_STYLES[assessment.status],
          )}
        >
          <StatusIcon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <div className="space-y-1">
            <p className="text-sm font-medium">{assessment.label}</p>
            <p className="text-xs">{assessment.description}</p>
          </div>
        </div>
      )}
      {!latest && (
        <p className="text-xs text-muted-foreground">
          This screening has not been run.
        </p>
      )}
      {screening.identityChanged && (
        <p role="note" className="text-xs text-amber-900">
          Provider details have changed since this check. The saved result
          applies to the names checked below. Run verification again to screen
          the current identity.
        </p>
      )}
      {screening.unavailableReason && (
        <p className="text-xs text-muted-foreground">
          {screening.unavailableReason}
        </p>
      )}
      {latest && (
        <>
          <ScreeningDetails
            latest={latest}
            sourceLabel={screening.label}
            program={program}
            issues={issues}
          />
          <ScreeningEvidence
            key={latest.checkId}
            latest={latest}
            scope={scope}
            sourceLabel={screening.label}
            program={program}
          />
        </>
      )}
      {imported && (
        <div className="space-y-1 border-t pt-2 text-xs">
          <p className="font-medium">
            Imported from{" "}
            {imported.vendor === "baton" ? "Baton" : imported.vendor}
          </p>
          <p>{IMPORTED_RESULT_LABELS[imported.result]}</p>
          <p className="text-muted-foreground">
            Imported {formatCredentialTimestamp(imported.importedAt)}. This is
            vendor information, separate from a direct screening.
          </p>
          <VerificationSourceLink sourceUrl={imported.sourceUrl} />
        </div>
      )}
    </section>
  );
}
